import { Form, Formik } from 'formik'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import * as Yup from 'yup'

import { editProfile, logout } from '../../redux/authSlice'
import { useAppHistory } from '../../utils/useAppHistory'
import { useIsDesktop } from '../../utils/useBreakpoint'
import { Input, Textarea } from '../form/FormikWrappers'
import AboutSection from '../mobile/AboutSection'
import MobileAppLinks from '../mobile/MobileAppLinks'
import MobileSocialLinks from '../mobile/MobileSocialLinks'
import Button from '../ui/Button'
import Checkbox from '../ui/Checkbox'
import FormButtons from '../ui/FormButtons'
import { PageHeader } from '../ui/Headers'
import LabeledRow from '../ui/LabeledRow'
import Column from '../ui/LinkColumn'
import LoadingIndicator from '../ui/LoadingIndicator'
import { TopSafeAreaInsetPage } from '../ui/PageTemplate'
import SubmitButton from '../ui/SubmitButton'
import withRedirectToAuth from './withRedirectToAuth'

const EmailLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
`

const EmailDisplay = styled.div`
  margin-bottom: 1.5em;
`

const PreferencesContainer = styled.div`
  margin-bottom: 1.5em;
  opacity: ${(props) => (props.$isDisabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.$isDisabled ? 'none' : 'auto')};
`

const LinksContainer = styled.div`
  margin-bottom: 1.5em;
`

const LinkSpacer = styled.div`
  margin-bottom: 1em;
`

const formToUser = ({ email, name, bio, announcements_email, range }) => ({
  email,
  name: name || null,
  bio: bio || null,
  announcements_email: announcements_email,
  range: range,
})

const userToForm = (user) => ({
  ...user,
  name: user.name ?? '',
  bio: user.bio ?? '',
  announcements_email: user.announcements_email,
})

const AccountPage = () => {
  const dispatch = useDispatch()
  const { user } = useSelector((state) => state.auth)
  const history = useAppHistory()
  const location = useLocation()
  const bottomRef = useRef(null)
  const { t } = useTranslation()
  const isDesktop = useIsDesktop()
  const [isUpdatingPreferences, setIsUpdatingPreferences] = useState(false)

  useEffect(() => {
    if (location.state?.scrollToBottom && user && !isDesktop) {
      bottomRef.current?.scrollIntoView()
    }
  }, [location.state, user, isDesktop])

  const handleSubmit = (values) => {
    dispatch(editProfile(formToUser(values)))
  }

  const handleAnnouncementsToggle = (checked) => {
    if (!user) {
      return
    }
    setIsUpdatingPreferences(true)
    const updatedUser = {
      email: user.email,
      announcements_email: checked,
      name: user.name || null,
      bio: user.bio || null,
      range: null,
    }
    dispatch(editProfile(updatedUser)).then(() => {
      setIsUpdatingPreferences(false)
    })
  }

  return (
    <TopSafeAreaInsetPage>
      <PageHeader>{t('glossary.account')}</PageHeader>

      {user ? (
        <>
          <section>
            <EmailLabel>{t('glossary.email')}</EmailLabel>
            <EmailDisplay>{user.email}</EmailDisplay>
            <PreferencesContainer $isDisabled={isUpdatingPreferences}>
              <LabeledRow
                label={
                  <label htmlFor="announcements_email_toggle">
                    {t('users.options.announcements_email')}
                  </label>
                }
                left={
                  <Checkbox
                    type="checkbox"
                    id="announcements_email_toggle"
                    checked={user.announcements_email}
                    disabled={isUpdatingPreferences}
                    onChange={(e) =>
                      handleAnnouncementsToggle(e.target.checked)
                    }
                  />
                }
              />
            </PreferencesContainer>
            <Button
              onClick={() => {
                dispatch(logout())
                history.push('/map')
              }}
            >
              {t('users.sign_out')}
            </Button>
            <LinksContainer>
              <Column>
                <Link to="/account/change-email">
                  {t('users.change_email')}
                </Link>
                <Link to="/account/change-password">
                  {t('users.change_password')}
                </Link>
                <Link to="/account/delete">{t('users.delete_account')}</Link>
                {!isDesktop && (
                  <>
                    <LinkSpacer />
                    <Link to={`/users/${user.id}/activity`}>
                      {t('users.your_activity')}
                    </Link>
                    <Link to="/account/lists">
                      {t('save_location_to_list.saved_locations_title')}
                    </Link>
                  </>
                )}
              </Column>
            </LinksContainer>
          </section>

          <section>
            <h2>{t('users.header.profile')}</h2>
            <Formik
              enableReinitialize
              initialValues={userToForm(user)}
              validationSchema={Yup.object({
                name: Yup.string(),
                bio: Yup.string(),
              })}
              onSubmit={handleSubmit}
            >
              {({ dirty, isValid, isSubmitting }) => (
                <Form>
                  <div>
                    <Input type="text" name="name" label={t('glossary.name')} />
                    <Textarea name="bio" label={t('users.bio')} />
                  </div>

                  <FormButtons>
                    <Button secondary type="reset">
                      {t('form.button.reset')}
                    </Button>
                    <SubmitButton
                      isSubmitting={isSubmitting}
                      disabled={!dirty || !isValid}
                      label={t('users.save_changes')}
                    />
                  </FormButtons>
                </Form>
              )}
            </Formik>
          </section>
          {!isDesktop && (
            <section>
              <MobileAppLinks />
              <AboutSection />
              <br />
              <MobileSocialLinks />
              <div ref={bottomRef} />
            </section>
          )}
        </>
      ) : (
        <LoadingIndicator vertical cover />
      )}
    </TopSafeAreaInsetPage>
  )
}

export default withRedirectToAuth(AccountPage)
